import { useCallback, useRef, useState } from "react";
import { buildRequestInit } from "@/api/httpClient";
import { config } from "@/config";
import type { MessageCreate, Session } from "@/types";

/** One decoded SSE event from `POST /sessions/{id}/messages/stream`. Legacy parity:
 * `event: token|agent_event|done|error` (see `app/routers/chat_messages.py`). */
export type ChatStreamEvent =
  | { type: "token"; content: string }
  | { type: "agent_event"; data: unknown }
  | { type: "done"; session: Session }
  | { type: "error"; message: string };

type ChatStreamState = {
  isStreaming: boolean;
  streamedText: string;
  agentEvents: unknown[];
  error: string | null;
};

const INITIAL_STATE: ChatStreamState = {
  isStreaming: false,
  streamedText: "",
  agentEvents: [],
  error: null,
};

function parseSseChunk(chunk: string): ChatStreamEvent[] {
  const events: ChatStreamEvent[] = [];
  for (const block of chunk.split("\n\n")) {
    if (!block.trim()) continue;
    let eventName = "message";
    let data = "";
    for (const line of block.split("\n")) {
      if (line.startsWith("event:"))
        eventName = line.slice("event:".length).trim();
      else if (line.startsWith("data:"))
        data = line.slice("data:".length).trim();
    }
    if (!data) continue;
    try {
      if (eventName === "token")
        events.push({ type: "token", content: JSON.parse(data) as string });
      else if (eventName === "agent_event")
        events.push({ type: "agent_event", data: JSON.parse(data) });
      else if (eventName === "done")
        events.push({ type: "done", session: JSON.parse(data) as Session });
      else if (eventName === "error")
        events.push({ type: "error", message: JSON.parse(data) as string });
    } catch {
      // Ignore malformed frames rather than aborting the whole stream.
    }
  }
  return events;
}

/** Typed SSE hook: sends a message to `POST /sessions/{id}/messages/stream` and exposes the
 * incremental token text + reasoning/tool events as they arrive, until `done`/`error`. */
export function useChatStream(
  sessionId: string,
  onDone: (session: Session) => void,
) {
  const [state, setState] = useState<ChatStreamState>(INITIAL_STATE);
  const abortRef = useRef<AbortController | null>(null);

  const send = useCallback(
    async (payload: MessageCreate) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setState({
        isStreaming: true,
        streamedText: "",
        agentEvents: [],
        error: null,
      });

      try {
        const init = await buildRequestInit({
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
        const response = await fetch(
          `${config.apiBase}/sessions/${encodeURIComponent(sessionId)}/messages/stream`,
          init,
        );
        if (!response.ok || !response.body) {
          throw new Error(
            `POST /sessions/${sessionId}/messages/stream failed: ${response.status}`,
          );
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { value, done: streamDone } = await reader.read();
          if (streamDone) break;
          buffer += decoder.decode(value, { stream: true });
          const frames = buffer.split("\n\n");
          buffer = frames.pop() ?? "";
          for (const event of parseSseChunk(frames.join("\n\n"))) {
            if (event.type === "token") {
              setState((prev) => ({
                ...prev,
                streamedText: prev.streamedText + event.content,
              }));
            } else if (event.type === "agent_event") {
              setState((prev) => ({
                ...prev,
                agentEvents: [...prev.agentEvents, event.data],
              }));
            } else if (event.type === "done") {
              setState((prev) => ({ ...prev, isStreaming: false }));
              onDone(event.session);
            } else if (event.type === "error") {
              setState((prev) => ({
                ...prev,
                isStreaming: false,
                error: event.message,
              }));
            }
          }
        }
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          error: prev.error ?? "The stream connection failed.",
        }));
      } finally {
        setState((prev) =>
          prev.isStreaming ? { ...prev, isStreaming: false } : prev,
        );
      }
    },
    [sessionId, onDone],
  );

  return { ...state, send };
}
